import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query"
import { createContext, useContext, useRef, ReactNode } from "react"

import apiClient from "@api/client"
import { QUERY_KEYS } from "@api/constants"
import { ONE_MINUTE } from "@constants/time"
import { FulfillmentType, Order, OrderScheduleType } from "@models/order"
import { OrderItem } from "@models/orderItem"

import { useActiveStore } from "../hooks/useActiveStore"

import { IdentityContext } from "./IdentityProvider"

const scheduleTypeToString = (type: OrderScheduleType): string => {
  switch (type) {
    case OrderScheduleType.asap:
      return "ASAP"
    case OrderScheduleType.fixedTime:
      return "FIXED_TIME"
  }
}

interface CartContextType {
  cart: UseQueryResult<Order>
  setQuantity: (orderItemId: number, quantity: number) => void
  updateOrderItemQuantity: ReturnType<
    typeof useMutation<
      unknown,
      Error,
      { orderItemId: number; quantity: number },
      unknown
    >
  >
  setFulfillmentType: ReturnType<
    typeof useMutation<
      Order,
      Error,
      {
        fulfillmentType: FulfillmentType
        isCurbside: boolean
        curbsidePickupVehicleMakeModel: string
        curbsidePickupVehicleColor: string
        curbsidePickupVehicleNotes: string
      },
      unknown
    >
  >
  setStoreTip: ReturnType<typeof useMutation<unknown, Error, number, unknown>>
  setCourierTip: ReturnType<typeof useMutation<unknown, Error, number, unknown>>
  setSchedule: ReturnType<
    typeof useMutation<
      unknown,
      Error,
      {
        date: string
        startTime: string
        endTime: string
        scheduleType: OrderScheduleType
        storeId?: number
      },
      unknown
    >
  >
  submitOrder: ReturnType<
    typeof useMutation<
      { order_total: { total: number }; id: number; message?: string } | null,
      Error,
      string | null,
      unknown
    >
  >
  applyPromoCode: ReturnType<
    typeof useMutation<unknown, Error, { promoCode: string }, unknown>
  >
  removePromo: ReturnType<
    typeof useMutation<unknown, Error, { promoId: number }, unknown>
  >
  setApplyCredits: ReturnType<
    typeof useMutation<unknown, Error, boolean, unknown>
  >
  updatePackagingPreferences: ReturnType<
    typeof useMutation<unknown, Error, boolean, unknown>
  >
}

const CartContext = createContext<CartContextType | undefined>(undefined)

interface CartProviderProps {
  children: ReactNode
}

export const CartProvider = ({ children }: CartProviderProps) => {
  const identity = useContext(IdentityContext)
  const queryClient = useQueryClient()
  const { activeStore } = useActiveStore()
  const debounceTimeoutsRef = useRef<Map<number, NodeJS.Timeout>>(new Map())

  const storeId = activeStore?.id

  const order = useQuery({
    queryKey: [QUERY_KEYS.GET_CART, storeId],
    staleTime: apiClient.hasToken ? ONE_MINUTE : undefined,
    refetchOnWindowFocus: "always",
    queryFn: async () => {
      const res = await apiClient.get(`stores/${storeId}/cart/`)
      if (res) return Order.fromDynamic(res.body as { [key: string]: never })
      throw Error()
    },
    enabled: Boolean(identity.identity && !!storeId && apiClient.hasToken),
  })

  const updateOrderItemQuantity = useMutation<
    unknown,
    Error,
    { orderItemId: number; quantity: number },
    unknown
  >({
    mutationFn: async ({ orderItemId, quantity }) => {
      await apiClient.put(
        `stores/${activeStore?.id}/cart/update-item-quantity/`,
        {
          orderItemId,
          quantity,
        },
      )
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_CART] })
    },
  })

  const setQuantity = (orderItemId: number, quantity: number) => {
    // Optimistically update the query result
    queryClient.setQueryData(
      [QUERY_KEYS.GET_CART, activeStore?.id],
      (oldData: Order | undefined) => {
        if (!oldData) return oldData
        const newCart = { ...oldData }
        const i = newCart.orderItems?.findIndex((oi) => oi.id === orderItemId)
        if (i === -1) return oldData

        // Create a new orderItems array with the updated item
        const newOrderItems = [...newCart.orderItems]
        if (quantity === 0) {
          newOrderItems.splice(i, 1)
        } else {
          // Create a new OrderItem instance with the updated quantity
          const oldItem = newOrderItems[i]
          newOrderItems[i] = new OrderItem(
            oldItem.id,
            oldItem.createdAt,
            oldItem.orderId,
            oldItem.menuItemId,
            oldItem.menuId,
            oldItem.orderItemOptions,
            quantity, // Updated quantity
            oldItem.quantityAdjustments,
            oldItem.price,
            oldItem.name,
            oldItem.isReward,
            oldItem.itemImgSrc,
            oldItem.categoryName,
            oldItem.specialInstructions,
            oldItem.refunded,
            oldItem.refundType,
            oldItem.refundReason,
            oldItem.refundNote,
            oldItem.rewardPointsUsed,
            oldItem.isAvailable,
          )
        }

        // Create a new cart object with the new orderItems array
        const updatedCart = { ...newCart, orderItems: newOrderItems }
        return updatedCart
      },
    )

    // Clear existing timeout for this specific orderItemId
    const existingTimeout = debounceTimeoutsRef.current.get(orderItemId)
    if (existingTimeout) {
      clearTimeout(existingTimeout)
    }

    // Set new timeout for API call for this specific orderItemId
    const newTimeout = setTimeout(() => {
      updateOrderItemQuantity.mutate(
        { orderItemId, quantity },
        {
          onSuccess: () => {
            identity.refresh()
          },
        },
      )
      // Clean up the timeout reference after it executes
      debounceTimeoutsRef.current.delete(orderItemId)
    }, 750)

    debounceTimeoutsRef.current.set(orderItemId, newTimeout)
  }

  const updatePackagingPreferences = useMutation<
    unknown,
    Error,
    boolean,
    unknown
  >({
    mutationFn: async (includeUtensils: boolean) => {
      await apiClient.post(
        `stores/${activeStore?.id}/cart/edit-packaging-preferences/`,
        {
          includeUtensils: includeUtensils,
        },
      )
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_CART] })
    },
  })

  const setFulfillmentType = useMutation<
    Order,
    Error,
    {
      fulfillmentType: FulfillmentType
      isCurbside: boolean
      curbsidePickupVehicleMakeModel: string
      curbsidePickupVehicleColor: string
      curbsidePickupVehicleNotes: string
    },
    unknown
  >({
    mutationFn: async ({
      fulfillmentType,
      isCurbside,
      curbsidePickupVehicleMakeModel,
      curbsidePickupVehicleColor,
      curbsidePickupVehicleNotes,
    }) => {
      const res = await apiClient.put(
        `stores/${activeStore?.id}/cart/edit-delivery-type/`,
        {
          fulfillmentType,
          curbside_pickup_vehicle_make_model: curbsidePickupVehicleMakeModel,
          curbside_pickup_vehicle_color: curbsidePickupVehicleColor,
          curbside_pickup_vehicle_notes: curbsidePickupVehicleNotes,
          is_curbside: isCurbside,
        },
      )

      if (res?.statusCode != 200) {
        const bodyAsErrorMessage = (res?.body ?? {}) as { message?: string }

        if (bodyAsErrorMessage.message) {
          throw Error(
            bodyAsErrorMessage.message != ""
              ? bodyAsErrorMessage.message
              : "Delivery is not available to selected address.",
          )
        } else {
          throw Error("Delivery is not available to selected address.")
        }
      }
      return Order.fromDynamic(res.body as { [key: string]: never })
    },
    onSuccess: (data) => {
      queryClient.setQueryData([QUERY_KEYS.GET_CART, activeStore?.id], data)
    },
  })

  const setStoreTip = useMutation<unknown, Error, number, unknown>({
    mutationFn: async (tipCents: number) => {
      await apiClient.put(`stores/${activeStore?.id}/cart/edit-store-tip/`, {
        storeTip: tipCents,
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_CART] })
    },
  })

  const setCourierTip = useMutation<unknown, Error, number, unknown>({
    mutationFn: async (tipCents: number) => {
      await apiClient.put(`stores/${activeStore?.id}/cart/edit-courier-tip/`, {
        courierTip: tipCents,
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_CART] })
    },
  })

  const submitOrder = useMutation({
    mutationFn: async (paymentMethodId: string | null) => {
      const response = await apiClient.post<
        { order_total: { total: number }; id: number; message?: string },
        {
          stripe_payment_method_id: string | null
        }
      >(`stores/${activeStore?.id}/cart/submit/`, {
        stripe_payment_method_id: paymentMethodId,
      })

      if (response?.statusCode != 200) {
        if (response?.body && response.body?.["message"]) {
          throw new Error(
            response.body["message"] != ""
              ? response.body["message"]
              : "We were unable to process your request. Please try again later.",
          )
        } else {
          // throw a more generic error
          throw new Error(
            "We were unable to process your request. Please try again later.",
          )
        }
      }

      return response?.body ? response.body : null
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_CURRENT_ORDERS],
      })
    },
    onSettled: (_, error) => {
      if (!error) {
        queryClient.resetQueries({
          queryKey: [QUERY_KEYS.GET_CART],
        })
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_ORDERS],
        })
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_WALLETS],
        })
        queryClient.resetQueries({
          queryKey: [QUERY_KEYS.GET_CREDIT],
        })
        queryClient.resetQueries({
          queryKey: [QUERY_KEYS.GET_MERCHANT_LOYALTY_PROGRAM],
        })
        queryClient.resetQueries({
          queryKey: [QUERY_KEYS.GET_ACCUMULATION_LOYALTY_ACCOUNT],
        })
        queryClient.resetQueries({
          queryKey: [QUERY_KEYS.GET_ACCUMULATION_LOYALTY_TRANSACTIONS],
        })
      } else {
        // error refresh cart
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_CART],
        })
      }
    },
  })

  const setSchedule = useMutation<
    unknown,
    Error,
    {
      date: string
      startTime: string
      endTime: string
      scheduleType: OrderScheduleType
      storeId?: number
    },
    unknown
  >({
    mutationFn: async ({
      date,
      startTime,
      endTime,
      scheduleType,
      storeId = activeStore?.id,
    }) => {
      await apiClient.post(`stores/${storeId}/cart/edit-schedule-type/`, {
        type: scheduleTypeToString(scheduleType),
        window: {
          date,
          start_time: startTime,
          end_time: endTime,
        },
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_CART] })
    },
  })

  const applyPromoCode = useMutation<
    unknown,
    Error,
    {
      promoCode: string
    },
    unknown
  >({
    mutationFn: async ({ promoCode }) => {
      const res = await apiClient.post(
        `stores/${activeStore?.id}/cart/apply-promo-code/`,
        {
          promoCode,
        },
      )
      if (res?.statusCode !== 200)
        throw new Error(
          (res?.body as unknown as { error: string })?.error ??
            "Invalid promo code",
        )
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_CART] })
    },
  })

  const removePromo = useMutation<
    unknown,
    Error,
    {
      promoId: number
    },
    unknown
  >({
    mutationFn: async ({ promoId }) => {
      await apiClient.post(`stores/${activeStore?.id}/cart/remove-promo/`, {
        promoId,
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_CART] })
    },
  })

  const setApplyCredits = useMutation<unknown, Error, boolean, unknown>({
    mutationFn: async (applyCredits: boolean) => {
      await apiClient.post(
        `stores/${activeStore?.id}/cart/edit-apply-credits/`,
        {
          apply_credits: applyCredits,
        },
      )
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_CART] })
    },
  })

  const contextValue: CartContextType = {
    cart: order,
    setQuantity,
    updateOrderItemQuantity,
    setFulfillmentType,
    setStoreTip,
    setCourierTip,
    setSchedule,
    submitOrder,
    applyPromoCode,
    removePromo,
    setApplyCredits,
    updatePackagingPreferences,
  }

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  )
}

export const useCart = () => {
  const context = useContext(CartContext)
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider")
  }
  return context
}
